import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators,} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, concatMap, debounceTime, finalize, map, of, Subject, takeUntil,} from 'rxjs';

import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {BreadcrumbComponent} from '../../../../shared/components/breadcrumb/breadcrumb.component';
import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import {LoadingService} from '../../../../shared/services/loading.service';
import {UnitsService} from '../../../../shared/services/units.service';
import {UserService} from '../../../../shared/services/user.service';
import {IBreadcrumb} from '../../../../utils/interfaces/breadcrumb';
import {IPollToList} from '../../../../utils/interfaces/poll';
import {
  IAvailableTime,
  IAvailableTimeForm,
  IDevicesBranch,
  IDevicesForm,
  IUnit,
  IUnitForm,
  IUnitSuspiciousActivityConfig,
  IUnitUpdatePayload,
  IUserForm,
  IUsersBranch,
} from '../../../../utils/interfaces/unit';
import {IUser} from '../../../../utils/interfaces/user';
import {AlertService} from '../../../../shared/services/alert.service';
import {UnitIncentiveAddDialogComponent} from "./dialogs/unit-incentive-add.dialog/unit-incentive-add.dialog.component";
import {
  UnitIncentiveHistoryDialogComponent
} from "./dialogs/unit-incentive-history.dialog/unit-incentive-history.dialog.component";
import {PermissionsService} from '../../../../shared/services/permissions.service';
import {PollTypeEnum} from "../../../../utils/enums/poll-type.enum";
import {MatDivider} from "@angular/material/divider";
import {MatTooltipModule} from "@angular/material/tooltip";
import {
  UnitAvailableTimeSectionComponent
} from "./wrappers/unit-available-time-section/unit-available-time-section.component";
import {UnitDevicesSectionComponent} from "./wrappers/unit-devices-section/unit-devices-section.component";
import {UnitEditFormService} from "./services/unit-edit-form.service";
import {UnitPollAutocompleteComponent} from "./wrappers/unit-poll-autocomplete/unit-poll-autocomplete.component";

@Component({
  selector: 'app-unit-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDivider,
    UnitAvailableTimeSectionComponent,
    UnitDevicesSectionComponent,
    UnitPollAutocompleteComponent,
    MatTooltipModule,
  ],
  templateUrl: './unit-edit.component.html',
  styleUrl: './unit-edit.component.scss',
  providers: [UnitEditFormService]
})
export class UnitEditComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly alertService = inject(AlertService);
  private readonly userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly unitsService = inject(UnitsService);
  private readonly loadingService = inject(LoadingService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly unsubscribe$ = new Subject();

  private readonly unitEditFormService = inject(UnitEditFormService);
  private readonly suspiciousActivityEditPermission = 'sapore.survey.security.parameters.edit';
  private readonly suspiciousActivityViewPermission = 'sapore.survey.security.screen.view';

  protected readonly PollTypeEnum = PollTypeEnum;
  readonly canEditSuspiciousActivityConfig = this.permissionsService.checkHavePermissions(
    this.suspiciousActivityEditPermission
  );
  readonly canViewSuspiciousActivityConfig = this.canEditSuspiciousActivityConfig
    || this.permissionsService.checkHavePermissions(this.suspiciousActivityViewPermission);


  breadcrumb: IBreadcrumb[] = [
    {
      description: 'Unidades',
      url: 'main/registration/units',
      textTitle: 'Cancelar edição',
      descriptionMsg: 'Deseja realmente cancelar a edição da unidade?',
      askBeforeChangingRoute: true,
    },
    {
      description: 'Detalhes da unidade',
      url: '',
    },
  ];

  form!: FormGroup<IUnitForm>;
  searchUser = new FormControl<string>('');
  satisfactionSearchPoll = new FormControl<string>('', Validators.required);
  reservationSearchPoll = new FormControl<string>('');

  usersList: IUser[] = [];

  loadingUsers = false;
  unit!: IUnit;
  pollsSatisfaction: IPollToList[] = [];
  pollsReservation: IPollToList[] = [];
  isSearching = false;
  private suspiciousActivityConfigLoaded = false;

  ngOnInit(): void {
    this.initialValues();

    this.searchUser.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(debounceTime(700))
      .subscribe((value) => this.filterUsers(value, true));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  initialValues(): void {
    this.unit = this.activatedRoute.snapshot.data['unit'].data;
    this.pollsSatisfaction = this.activatedRoute.snapshot.data['pollsSatisfaction'].data;
    this.pollsReservation = this.activatedRoute.snapshot.data['pollsReservation'].data;

    this.form = new FormGroup<IUnitForm>({
      id: new FormControl<string>(this.unit.id),
      branchId: new FormControl<string>(this.unit.id),
      code: new FormControl<string>({value: this.unit.code, disabled: true}),
      name: new FormControl<string>({
        value: this.unit.name,
        disabled: true,
      }),
      region: new FormControl<string>({
        value: this.unit.region,
        disabled: true,
      }),
      active: new FormControl<boolean>({
        value: this.unit.active,
        disabled: true,
      }),
      customerName: new FormControl<string>({
        value: this.unit.customerName,
        disabled: true,
      }),
      shortBranch: new FormControl<string>({
        value: this.unit.shortBranch!,
        disabled: true,
      }),
      pollId: new FormControl<string>(
        this.unit.pollId ?? '',
        Validators.required
      ),
      pollDishReservationId: new FormControl<string | null>(
        this.unit.pollDishReservationId ?? null
      ),
      suspiciousActivityConfig: this.unitEditFormService.createSuspiciousActivityConfigForm(),
      satisfactionAvailableShifts: new FormArray<FormGroup<IAvailableTimeForm>>(
        this.unit.availableShifts
          ?.filter((item) => item.typePoll === PollTypeEnum.Satisfaction)
          ?.map((available: IAvailableTime) =>
            this.unitEditFormService.availableTimeCreate(available)
          )
      ),
      reservationAvailableShifts: new FormArray<FormGroup<IAvailableTimeForm>>(
        this.unit.availableShifts
          ?.filter((item) => item.typePoll === PollTypeEnum.Reservation)
          ?.map((available: IAvailableTime) =>
            this.unitEditFormService.availableTimeCreate(available)
          )
      ),
      usersBranch: new FormArray<FormGroup<IUserForm>>(
        this.unit.usersBranch.map((user: IUsersBranch) => this.createUser(user))
      ),
      satisfactionDevices: new FormArray<FormGroup<IDevicesForm>>(
        this.unit.devices
          ?.filter((device) => device.typePoll === PollTypeEnum.Satisfaction)
          ?.map((device: IDevicesBranch) =>
            this.unitEditFormService.deviceCreate(device)
          )
      ),
      reservationDevices: new FormArray<FormGroup<IDevicesForm>>(
        this.unit.devices
          ?.filter((device) => device.typePoll === PollTypeEnum.Reservation)
          ?.map((device: IDevicesBranch) =>
            this.unitEditFormService.deviceCreate(device)
          )
      ),
    });

    this.form.controls.suspiciousActivityConfig.disable({emitEvent: false});
    this.watchSuspiciousActivityRules();

    if (this.canViewSuspiciousActivityConfig) {
      this.loadSuspiciousActivityConfig();
    }

  }

  filterUsers(filter: string | null = null, search = false): void {
    this.loadingUsers = true;
    if (!filter) {
      this.usersList = [];
      this.isSearching = false;
      this.loadingUsers = false;

      return;
    }

    this.userService
      .getUsers(search, filter, 0, 0, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.usersList = res.data;
        this.isSearching = true;
        this.loadingUsers = false;
      });
  }

  checkAddUser(user: IUser): void {
    if (
      this.form.controls.usersBranch.value.findIndex(
        (item) => item.userId === user.id
      ) !== -1
    ) {
      this.alertService.danger('Esse usuário já foi adicionado.');

      return;
    }

    this.form.controls.usersBranch.push(
      this.createUser({
        userId: user.id,
        userName: user.name,
        userEmail: user.email,
      })
    );
  }

  deleteUser(id: string): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Excluir usuário',
        description: 'Deseja realmente excluir esse usuário?',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          const index = this.form.controls.usersBranch.value.findIndex(
            (item) => item.userId === id
          );

          this.form.controls.usersBranch.removeAt(index);
        }
      });
  }

  cancel(): void {
    const data: IBreadcrumb = this.breadcrumb[0];

    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: data.textTitle,
        description: data.descriptionMsg,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) this.router.navigate([data.url]).then(() => {
        });
      });
  }

  updateUnit(): void {
    if (this.form.invalid) {
      this.alertService.danger('Preencha os campos obrigatórios antes de atualizar a unidade.');
      this.form.markAllAsTouched();
      return;
    }

    const unitPayload = this.formToUnitUpdateValue();
    const suspiciousActivityConfig = this.formToSuspiciousActivityConfigValue();

    this.loadingService.showLoading();
    this.unitsService
      .updateUnit(unitPayload)
      .pipe(
        concatMap((unitResponse) => {
          if (!unitResponse.success) {
            return of<'unit-update-failed'>('unit-update-failed');
          }

          if (!this.canEditSuspiciousActivityConfig) {
            return of<'unit-only-success'>('unit-only-success');
          }

          if (!this.suspiciousActivityConfigLoaded) {
            return of<'success-without-suspicious-config'>('success-without-suspicious-config');
          }

          return this.unitsService
            .updateSuspiciousActivityConfig(unitPayload.branchId, suspiciousActivityConfig)
            .pipe(
              map((configResponse) =>
                configResponse.success
                  ? ('full-success' as const)
                  : ('suspicious-config-update-failed' as const)
              ),
              catchError(() => of<'suspicious-config-update-failed'>('suspicious-config-update-failed'))
            );
        }),
        finalize(() => this.loadingService.closeLoading()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (status) => {
          if (status === 'full-success' || status === 'unit-only-success') {
            this.alertService.success('Unidade atualizada com sucesso.');
            this.router.navigate(['main/registration/units']);

            return;
          }

          if (status === 'success-without-suspicious-config') {
            this.alertService.warning(
              'Unidade atualizada com sucesso. A configuração de atividade suspeita não foi salva porque não foi carregada.'
            );

            return;
          }

          if (status === 'suspicious-config-update-failed') {
            this.alertService.warning(
              'A unidade foi atualizada, mas houve falha ao salvar a configuração de atividade suspeita. Tente novamente.'
            );

            return;
          }

          this.alertService.danger('Não foi possível atualizar a unidade. Tente novamente.');
        },
        error: () => this.alertService.danger('Não foi possível atualizar a unidade. Tente novamente.'),
      });
  }

  shortNameHandler() {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      minWidth: '700px',
      data: {
        title: 'Deseja realmente alterar o nome curto da unidade?',
        description:
          'Ao alterar o nome curto da unidade, ele será atualizado em todos os locais onde é utilizado, e os QR Codes deixarão de funcionar. Será necessário gerar novos materiais com os QR Codes atualizados.',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) this.changeShortName();
      });
  }

  changeShortName(): void {
    this.unitsService
      .refreshShortBranch(this.unit.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.loadingService.showLoading();
          this.getUnit();
        },
      });
  }

  getUnit(): void {
    this.unitsService
      .getUnitById(this.unit.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.form.controls.shortBranch.setValue(res.data.shortBranch!);

            this.loadingService.closeLoading();
          }
        },
      });
  }

  incentiveAdd() {
    const dialogRef = this.dialog.open(UnitIncentiveAddDialogComponent, {
      width: '936px',
      maxWidth: '936px',
      disableClose: true,
      data: {unitId: this.unit.id},
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.alertService.success('Incentivo adicionado com sucesso.');
        }
      });
  }

  incentiveHistory() {
    this.dialog.open(UnitIncentiveHistoryDialogComponent, {
      width: '1026px',
      maxWidth: '1026px',
      data: {unitId: this.unit.id},
    });
  }

  private formToUnitUpdateValue(): IUnitUpdatePayload {
    return this.unitEditFormService.formToUnitUpdateValue(this.form);
  }

  private formToSuspiciousActivityConfigValue(): IUnitSuspiciousActivityConfig {
    return this.unitEditFormService.formToSuspiciousActivityConfigValue(this.form);
  }

  private watchSuspiciousActivityRules(): void {
    const suspiciousActivityConfig = this.form.controls.suspiciousActivityConfig.controls;

    suspiciousActivityConfig.level1.controls.active.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.unitEditFormService.syncSuspiciousActivityConfigState(this.form.controls.suspiciousActivityConfig));

    suspiciousActivityConfig.level2.controls.active.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.unitEditFormService.syncSuspiciousActivityConfigState(this.form.controls.suspiciousActivityConfig));

    suspiciousActivityConfig.level3.controls.active.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.unitEditFormService.syncLevel3Validators(suspiciousActivityConfig.level3));
  }

  private loadSuspiciousActivityConfig(): void {
    this.unitsService
      .getSuspiciousActivityConfig(this.unit.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            if (this.canEditSuspiciousActivityConfig) {
              this.form.controls.suspiciousActivityConfig.enable({emitEvent: false});
            }

            this.unitEditFormService.patchSuspiciousActivityConfigForm(
              this.form.controls.suspiciousActivityConfig,
              res.data
            );

            if (!this.canEditSuspiciousActivityConfig) {
              this.form.controls.suspiciousActivityConfig.disable({emitEvent: false});
            }

            this.suspiciousActivityConfigLoaded = true;
          }
        },
      });
  }

  private createUser(user: IUsersBranch): FormGroup<IUserForm> {
    return new FormGroup<IUserForm>({
      userId: new FormControl<string>(user.userId),
      userName: new FormControl<string>({
        value: user.userName,
        disabled: true,
      }),
      userEmail: new FormControl<string>({
        value: user.userEmail,
        disabled: true,
      }),
    });
  }
}
